/**
 * Data Quality Panel — dataset quality scores, drift alerts, baseline management
 * Phase 4.8-B — WQ-200
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSummary,
  type MonitoringSummary,
} from '../../services/dataQualityService';
import { fmt } from '../../utils/formatNumber';

const TIER_COLORS: Record<string, string> = {
  good: '#22c55e',
  warning: '#f59e0b',
  critical: '#ef4444',
  unknown: '#6b7280',
};

const DIMENSION_LABELS = ['Completeness', 'Accuracy', 'Consistency', 'Timeliness', 'Uniqueness'];

function RadarChart({ dimensions }: { dimensions: Record<string, number> }) {
  const size = 180;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 70;
  const levels = 4;
  const labels = DIMENSION_LABELS;
  const n = labels.length;
  const angleSlice = (2 * Math.PI) / n;

  const gridLines: React.ReactNode[] = [];
  for (let l = 1; l <= levels; l++) {
    const r = (radius / levels) * l;
    const pts = Array.from({ length: n }, (_, i) => {
      const a = angleSlice * i - Math.PI / 2;
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    }).join(' ');
    gridLines.push(
      <polygon key={`grid-${l}`} points={pts} fill="none"
        stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
    );
  }

  const axes = labels.map((_, i) => {
    const a = angleSlice * i - Math.PI / 2;
    return (
      <line key={`axis-${i}`}
        x1={cx} y1={cy}
        x2={cx + radius * Math.cos(a)} y2={cy + radius * Math.sin(a)}
        stroke="rgba(255,255,255,0.1)" strokeWidth={1} />
    );
  });

  const dimKeys = Object.keys(dimensions);
  const values = labels.map((label) => {
    const key = dimKeys.find(k => k.toLowerCase() === label.toLowerCase()) ?? '';
    return dimensions[key] ?? 0;
  });
  const dataPts = values.map((v, i) => {
    const a = angleSlice * i - Math.PI / 2;
    const r = (v / 100) * radius;
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(' ');

  const labelEls = labels.map((label, i) => {
    const a = angleSlice * i - Math.PI / 2;
    const lx = cx + (radius + 22) * Math.cos(a);
    const ly = cy + (radius + 22) * Math.sin(a);
    return (
      <text key={`lbl-${i}`} x={lx} y={ly}
        textAnchor="middle" dominantBaseline="middle"
        fill="rgba(255,255,255,0.6)" fontSize={10}>{label}</text>
    );
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {gridLines}
      {axes}
      <polygon points={dataPts} fill="rgba(0,212,255,0.15)" stroke="#00d4ff" strokeWidth={2} />
      {values.map((v, i) => {
        const a = angleSlice * i - Math.PI / 2;
        const r = (v / 100) * radius;
        return (
          <circle key={`dot-${i}`} cx={cx + r * Math.cos(a)} cy={cy + r * Math.sin(a)}
            r={3} fill="#00d4ff" />
        );
      })}
      {labelEls}
    </svg>
  );
}

const DataQualityPanel: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [summary, setSummary] = useState<MonitoringSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSummary(authFetch);
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quality data');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div className="loading-spinner" style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #00d4ff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass" style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ color: '#ef4444', marginBottom: 12 }}>{error}</p>
        <button onClick={fetchData} className="glass" style={{ padding: '6px 16px', cursor: 'pointer', color: '#00d4ff', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: 6 }}>
          Retry
        </button>
      </div>
    );
  }

  if (!summary) return null;

  const tierEntries = Object.entries(summary.tiers);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#00d4ff' }}>{summary.total_datasets}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Total Datasets</div>
        </div>
        {tierEntries.map(([tier, data]) => (
          <div key={tier} className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: TIER_COLORS[tier] || '#6b7280' }}>{data.count}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4, textTransform: 'capitalize' }}>{tier}</div>
          </div>
        ))}
        <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: summary.recent_drift_alerts.length > 0 ? '#f59e0b' : '#22c55e' }}>
            {summary.recent_drift_alerts.length}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Drift Alerts</div>
        </div>
      </div>

      {/* Quality Tiers Breakdown */}
      <div className="glass" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
          Quality Tier Distribution
        </h3>
        {tierEntries.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No quality profiles yet</p>
        ) : (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {tierEntries.map(([tier, data]) => {
              const pct = summary.total_datasets > 0 ? (data.count / summary.total_datasets) * 100 : 0;
              return (
                <div key={tier} style={{ flex: '1 1 200px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, textTransform: 'capitalize', color: TIER_COLORS[tier] || '#6b7280' }}>{tier}</span>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{fmt(pct, 0)}%</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: TIER_COLORS[tier] || '#6b7280', borderRadius: 3, transition: 'width 0.5s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sample Radar (placeholder dimensions) */}
      <div className="glass" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
          Quality Dimensions (Platform Average)
        </h3>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <RadarChart dimensions={{ completeness: 85, accuracy: 78, consistency: 92, timeliness: 70, uniqueness: 88 }} />
        </div>
      </div>

      {/* Recent Drift Alerts */}
      <div className="glass" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
          Recent Drift Alerts
        </h3>
        {summary.recent_drift_alerts.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No drift alerts detected</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Dataset</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Check</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Column</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Statistic</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Detected</th>
                </tr>
              </thead>
              <tbody>
                {summary.recent_drift_alerts.map((alert, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{alert.catalog_entry_id.slice(0, 8)}</td>
                    <td style={{ padding: '8px 12px' }}>{alert.check_type}</td>
                    <td style={{ padding: '8px 12px', color: 'rgba(255,255,255,0.6)' }}>{alert.column_name || '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#f59e0b' }}>{fmt(alert.drift_statistic, 3)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                      {new Date(alert.detected_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataQualityPanel;
